import React, {useEffect, useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {
	ButtonDropdown,
	DropdownList,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Modal,
	SearchField,
	SubHeading,
	Table,
	Text,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {AssociateBillToEntityModal_viewer} from './__generated__/AssociateBillToEntityModal_viewer.graphql';
import Util from '../../../shared/util/util';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import CreateBulkBillToMutation from '../../../../mutations/invoicing/CreateBulkBillToMutation';
import CreateBulkClientBillToMutation from '../../../../mutations/invoicing/CreateBulkClientBillToMutation';
import {
	Contact,
	fetchEconomicContacts,
	fetchQuickbooksContacts,
	fetchXeroContacts,
	filterContactsByIntegrationInstanceId,
} from '../ts/InvoiceIntegrationsUtils';
import InvoicingIntegrationIcon from '../ts/InvocingIntegrationIcon';

type AssociateBillToEntityModalProps = {
	closeModal: () => void;
	viewer: AssociateBillToEntityModal_viewer;
};

const AssociateBillToEntityModal = ({closeModal, viewer}: AssociateBillToEntityModalProps) => {
	const {formatMessage} = useIntl();
	const [searchValue, setSearchValue] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [contacts, setContacts] = useState<Contact[]>([]);
	const [initialContactsList, setInitialContactsList] = useState<Contact[]>([]);
	const billFroms = viewer.company?.billFroms || [];
	const associatedBillTos = viewer.company?.client?.clientBillTos;
	const [billFrom, setBillFrom] = useState(billFroms[0]);
	const alreadyImportedBillToExtRefs = useMemo(
		() => new Set(billFrom?.billTos.edges?.map(edge => edge?.node?.externalId)),
		[billFrom]
	);
	const alreadyImportedBillToEntities = useMemo(() => billFrom?.billTos.edges?.map(edge => edge?.node), [billFrom]);
	const alreadyAssociatedExternalIds = useMemo(
		() =>
			new Set(
				associatedBillTos?.edges?.map(edge => {
					const alreadyAssociatedBillToEntity = alreadyImportedBillToEntities?.find(
						entity => entity?.id === edge?.node?.billTo?.id
					);
					return alreadyAssociatedBillToEntity?.externalId;
				})
			),
		[billFrom]
	);
	const [selectedRows, setSelectedRows] = useState<Contact[]>([]);
	const [filteredRows, setFilteredRows] = useState<Contact[]>([]);
	const filteredContactsCount = contacts.length - filteredRows.length;
	const [xeroFetched, setXeroFetched] = useState(false);
	const [economicFetched, setEconomicFetched] = useState(false);
	const [quickbooksFetched, setQuickbooksFetched] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);

	useEffect(() => {
		setIsLoading(true);

		const onXeroSuccess = (newContacts: Contact[]) => {
			setInitialContactsList(prevValue => [...prevValue, ...newContacts]);
			setXeroFetched(true);
		};

		const onQuickbooksSuccess = (newContacts: Contact[]) => {
			setInitialContactsList(prevValue => [...prevValue, ...newContacts]);
			setEconomicFetched(true);
		};

		const onEconomicSuccess = (newContacts: Contact[]) => {
			setInitialContactsList(prevValue => [...prevValue, ...newContacts]);
			setQuickbooksFetched(true);
		};

		fetchXeroContacts().then(onXeroSuccess);
		fetchQuickbooksContacts().then(onQuickbooksSuccess);
		fetchEconomicContacts().then(onEconomicSuccess);
	}, []);

	useEffect(() => {
		if (xeroFetched && economicFetched && quickbooksFetched) {
			setIsLoading(false);
			const filteredContacts = filterContactsByIntegrationInstanceId(initialContactsList, billFrom);
			setContacts(filteredContacts);
			setFilteredRows(filteredContacts);
		}
	}, [initialContactsList, billFrom, xeroFetched, economicFetched, quickbooksFetched]);

	const associateBillTos = () => {
		const billToEntitiesToCreate = selectedRows.filter(row => !alreadyImportedBillToExtRefs.has(row.id));
		const billToEntitiesToAssociate = selectedRows
			.filter(row => alreadyImportedBillToExtRefs.has(row.id))
			.map(entity => {
				const billToEntity = billFrom?.billTos.edges?.find(edge => edge?.node?.externalId === entity.id);
				return {id: billToEntity?.node?.id, ...billToEntity?.node};
			});

		const associateBillTosToClient = response => {
			if (response) {
				const createdBillTos: Array<Contact> = response.createBulkBillTo.billTos.map(billTo => billTo.node);

				for (let i = 0; i < createdBillTos.length; i++) {
					billToEntitiesToAssociate.push(createdBillTos[i]);
				}
			}

			Util.CommitMutation(
				CreateBulkClientBillToMutation,
				{
					billTos: billToEntitiesToAssociate.map(row => ({
						clientId: viewer.company?.client?.id,
						billToId: row.id,
						billFromId: billFrom?.id,
					})),
				},
				closeModal
			);
		};

		if (billToEntitiesToCreate.length) {
			Util.CommitMutation(
				CreateBulkBillToMutation,
				{
					billTos: billToEntitiesToCreate.map(row => ({
						name: row.name,
						address: row.address,
						taxId: row.taxId,
						externalId: row.id,
						billFromId: billFrom?.id,
					})),
				},
				associateBillTosToClient
			);
		} else if (billToEntitiesToAssociate.length) {
			// No billTo entities to create, just associate.
			associateBillTosToClient(null);
		} else {
			closeModal();
		}
	};

	const associateBillTosConfirmation = () => {
		const billToEntitiesToCreate = selectedRows.filter(row => !alreadyImportedBillToExtRefs.has(row.id));

		if (billToEntitiesToCreate.length) {
			setShowConfirmation(true);
		} else {
			associateBillTos();
		}
	};

	const goBackButtonHandler = () => {
		setShowConfirmation(false);
	};

	const calculateNoOfCustomers = billFrom => {
		return isLoading
			? ''
			: `(${
					initialContactsList.filter(
						contact =>
							(!contact.integrationInstanceId || contact.integrationInstanceId === billFrom?.externalId) &&
							contact.integration === billFrom?.integration
					).length
			  })`;
	};

	const BillFromItem = ({billFrom}) => (
		<FlexRow gap={'s'}>
			<InvoicingIntegrationIcon integration={billFrom?.integration} />
			<div>
				{billFrom?.name} {calculateNoOfCustomers(billFrom)}
			</div>
		</FlexRow>
	);

	const alreadyImportedAndAssignedIcon = {
		label: formatMessage({id: 'settings_finance.bill_to.already_associated'}),
		icon: 'checkCircle',
		color: 'success',
	};

	const notImportedYetIcon = {
		label: formatMessage({id: 'settings_finance.bill_to.not_imported_yet'}),
		icon: 'alert',
		color: 'warning',
	};

	return (
		<>
			<Modal onCloseModal={closeModal}>
				<div style={{display: showConfirmation === true ? 'contents' : 'none'}}>
					<Modal.Title>
						<FormattedMessage id="settings_finance.associate_bill_to_entities" />
					</Modal.Title>
					<Modal.Content>
						<Text>{formatMessage({id: 'settings_finance.bill_to.confirmation_message'})}</Text>
					</Modal.Content>
					<Modal.Footer>
						<Modal.PrimaryFooterButton onPress={associateBillTos}>
							<FormattedMessage id={'settings_finance.bill_to.confirmation_message_button'} />
						</Modal.PrimaryFooterButton>
						<Modal.SecondaryFooterButton onPress={goBackButtonHandler}>
							<FormattedMessage id={'common.go_back'} />
						</Modal.SecondaryFooterButton>
						<Modal.TertiaryFooterButton onPress={closeModal}>
							<FormattedMessage id={'common.cancel'} />
						</Modal.TertiaryFooterButton>
					</Modal.Footer>
				</div>
				<div style={{display: showConfirmation === false ? 'contents' : 'none'}}>
					<Modal.Title>
						<FormattedMessage id="settings_finance.associate_bill_to_entities" />
					</Modal.Title>
					<Modal.Content>
						<SearchField
							isExpandable={false}
							value={searchValue}
							onChange={setSearchValue}
							placeholder={formatMessage({id: 'common.search'})}
						/>

						{billFroms.length === 1 ? (
							<Label>
								<BillFromItem billFrom={billFrom} />
							</Label>
						) : (
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown emphasis={'medium'} style={{width: 'fit-content'}}>
										<BillFromItem billFrom={billFrom} />
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={5} align={'start'}>
									{billFroms.map(current => (
										<DropdownList.Item key={current?.id} onClick={() => setBillFrom(current)}>
											<FlexRow justifyContent={'space-between'}>
												<BillFromItem billFrom={current} />
												{current?.id === billFrom?.id && <Icon icon={'iconTick'} />}
											</FlexRow>
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						)}

						{isLoading ? (
							<InlineLoader />
						) : (
							<>
								<div>
									<Table
										data={contacts}
										searchValue={searchValue}
										onSelectionChange={setSelectedRows}
										onFilteringChange={setFilteredRows}
										enableRowSelection={row => !alreadyAssociatedExternalIds.has(row.original.id)}
										width={'100%'}
									>
										<Table.TextColumn
											header={formatMessage({id: 'common.select_all'})}
											accessorKey={'name'}
										/>
										<Table.IconColumn
											accessorKey={'id'}
											header={'Status'}
											hideHeader
											align={Table.Align.right}
											icon={(value: string) =>
												alreadyAssociatedExternalIds.has(value)
													? alreadyImportedAndAssignedIcon
													: !alreadyImportedBillToExtRefs.has(value)
													? notImportedYetIcon
													: undefined
											}
										/>
									</Table>
								</div>
								<FlexColumn alignItems={'center'}>
									{filteredRows.length === 0 && (
										<SubHeading size={'2'}>
											<FormattedMessage id={'settings_finance.bill_to.no_results'} />
										</SubHeading>
									)}
									{filteredContactsCount > 0 && (
										<Text>
											<FormattedMessage
												id={'settings_finance.bill_to.filtering_count'}
												values={{count: filteredContactsCount}}
											/>
										</Text>
									)}
								</FlexColumn>
							</>
						)}
					</Modal.Content>
					<Modal.Footer>
						<Modal.PrimaryFooterButton onPress={associateBillTosConfirmation} disabled={!selectedRows.length}>
							<FormattedMessage
								id={'settings_finance.bill_to.associate_selected'}
								values={{count: selectedRows.length}}
							/>
						</Modal.PrimaryFooterButton>
						<Modal.SecondaryFooterButton onPress={closeModal}>
							<FormattedMessage id={'common.close'} />
						</Modal.SecondaryFooterButton>
					</Modal.Footer>
				</div>
			</Modal>
		</>
	);
};

export const AssociateBillToEntityModalQuery = graphql`
	query AssociateBillToEntityModalQuery($clientId: String) {
		viewer {
			actualPersonId
			component(name: "AssociateBillToEntityModal")
			...AssociateBillToEntityModal_viewer @arguments(clientId: $clientId)
		}
	}
`;

export default createFragmentContainer(AssociateBillToEntityModal, {
	viewer: graphql`
		fragment AssociateBillToEntityModal_viewer on Viewer @argumentDefinitions(clientId: {type: "String"}) {
			id
			company {
				id
				billFroms {
					id
					integration
					externalId
					name
					billTos(first: 10000) @connection(key: "BillFrom_billTos", filters: []) {
						edges {
							node {
								id
								name
								address
								taxId
								externalId
							}
						}
					}
				}
				client(id: $clientId) {
					id
					clientBillTos(first: 10000) @connection(key: "Client_clientBillTos", filters: []) {
						edges {
							node {
								id
								billFrom {
									id
								}
								billTo {
									id
								}
								clientId
							}
						}
					}
				}
			}
		}
	`,
});
